/*!
 * Projects lightbox — <projects-lightbox> peste <dialog> nativ.
 *
 * Declansatoarele stau oriunde in pagina si poarta
 * `data-projects-lightbox-open="{id}"` plus datele imaginii in data-lb-*.
 * Sunt citite din tot documentul, deci sectiunea care le randeaza nu trebuie sa
 * le tina in acelasi container ca dialogul.
 *
 * Imaginile mari nu se descarca la incarcarea paginii: `src`/`srcset` se pun pe
 * <img> abia la deschidere, iar hover-ul pe un card face prefetch.
 *
 * La fiecare schimbare emite `projects-lightbox:change` cu { index }, ca un
 * carusel-gazda sa poata urmari proiectul afisat.
 */
(function () {
  class ProjectsLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || this.initialized) return;

      // Fara showModal() nu exista focus trap; mai bine nu deschidem deloc.
      if (typeof this.dialog.showModal !== 'function') return;

      this.openers = Array.from(document.querySelectorAll('[data-projects-lightbox-open="' + this.id + '"]'));
      if (!this.openers.length) return;

      this.initialized = true;

      this.frame = this.dialog.querySelector('[data-lb-frame]');
      this.image = this.dialog.querySelector('[data-lb-image]');
      this.video = this.dialog.querySelector('[data-lb-video]');
      this.titleEl = this.dialog.querySelector('[data-lb-title]');
      this.subtitleEl = this.dialog.querySelector('[data-lb-subtitle]');
      this.linkEl = this.dialog.querySelector('[data-lb-link]');
      this.countEl = this.dialog.querySelector('[data-lb-count]');
      this.prevBtn = this.dialog.querySelector('[data-lb-prev]');
      this.nextBtn = this.dialog.querySelector('[data-lb-next]');
      this.index = 0;

      this.openers.forEach((opener, i) => {
        opener.addEventListener('click', () => this.open(i));
        // Prefetch pe intentie ferma, nu pe hover: o simpla trecere cu mouse-ul
        // peste un rand de carduri descarca altfel toate imaginile mari degeaba.
        opener.addEventListener('pointerdown', () => this.prefetch(i), { once: true });
        opener.addEventListener('focus', () => this.prefetch(i), { once: true });
      });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.show(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.show(this.index + 1));

      this.dialog.querySelectorAll('[data-lb-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.dialog.close());
      });

      // Click pe fundal: <dialog> primeste evenimentul, continutul il opreste.
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.dialog.close();
      });

      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.show(this.index - 1);
        if (e.key === 'ArrowRight') this.show(this.index + 1);
      });

      this.dialog.addEventListener('close', () => {
        // Clipul nu trebuie sa mearga mai departe cu sonorul dupa inchidere.
        if (this.video) {
          this.video.pause();
          this.video.removeAttribute('src');
          this.video.innerHTML = '';
          this.video.load();
        }

        // Focusul se intoarce pe cardul afisat, nu pe inceputul paginii.
        const opener = this.openers[this.index];
        if (opener) opener.focus();

        // Anuntat la INCHIDERE, nu la fiecare schimbare: cat timp dialogul e
        // modal, browserul refuza scroll-ul programatic al paginii din spate.
        // Amanat un cadru fiindca in handler-ul de `close` modalitatea e inca
        // activa, deci un scrollTo chemat aici tot ar fi ignorat.
        requestAnimationFrame(() => {
          this.dispatchEvent(
            new CustomEvent('projects-lightbox:close', { bubbles: true, detail: { index: this.index } })
          );
        });
      });

      // Cadrul e dimensionat in px, deci trebuie recalculat la rotirea ecranului.
      this.onResize = () => {
        this.limits = null;
        if (!this.dialog.open) return;
        window.cancelAnimationFrame(this.raf);
        this.raf = window.requestAnimationFrame(() => this.show(this.index));
      };
      window.addEventListener('resize', this.onResize, { passive: true });

      this.setupDrag();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.cancelAnimationFrame(this.raf);
    }

    // Limitele vin din CSS, ca sa existe o singura sursa de adevar. Citirea lor
    // forteaza recalcul de stil, iar `show()` are nevoie de ele de trei ori
    // (imaginea curenta + cei doi vecini), deci se tin in cache pana la resize.
    cssLimits() {
      if (!this.limits) {
        this.limits = {
          maxW: parseFloat(getComputedStyle(this.dialog).maxWidth) || window.innerWidth,
          maxH: parseFloat(getComputedStyle(this.image).maxHeight) || window.innerHeight,
        };
      }
      return this.limits;
    }

    // Latimea la care va fi chiar afisata imaginea. Un `sizes` fix ar cere mereu
    // cat tine dialogul, desi o poza portret e limitata de inaltime, nu de latime.
    renderedWidth(opener) {
      const { maxW, maxH } = this.cssLimits();
      const w = parseInt(opener.dataset.lbW, 10);
      const h = parseInt(opener.dataset.lbH, 10);
      if (!w || !h) return Math.round(maxW);
      return Math.round(Math.min(maxW, maxH * (w / h)));
    }

    // Trage in cache imaginea mare a unui proiect, fara sa o afiseze.
    prefetch(index) {
      const opener = this.openers[index];
      if (!opener || opener.dataset.lbPrefetched) return;
      // Clipurile nu se pre-descarca: ar fi megabytes pentru un card poate nedeschis.
      if (opener.dataset.lbVideoSources) return;
      opener.dataset.lbPrefetched = '1';
      const img = new Image();
      img.sizes = this.renderedWidth(opener) + 'px';
      img.srcset = opener.dataset.lbSrcset || '';
      img.src = opener.dataset.lbSrc || '';
    }

    // Drag/swipe orizontal peste imagine = proiectul anterior/urmator.
    setupDrag() {
      const frame = this.frame;
      if (!frame) return;

      const THRESHOLD = 50;
      const DRAG_THRESHOLD = 4;
      let startX = 0;
      let down = false;
      let dragging = false;

      frame.addEventListener('pointerdown', (e) => {
        // Butoanele din cadru isi pastreaza click-ul; nu sunt zona de tras.
        if (e.target.closest('button')) return;
        down = true;
        dragging = false;
        startX = e.clientX;
      });

      frame.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;

        // Capture doar dupa ce tragerea a inceput, altfel click-ul ar fi
        // retargetat catre cadru si butoanele dinauntru ar deveni inerte.
        if (!dragging) {
          if (Math.abs(dx) <= DRAG_THRESHOLD) return;
          dragging = true;
          frame.classList.add('is-dragging');
          try {
            frame.setPointerCapture(e.pointerId);
          } catch (err) {
            /* pointer deja eliberat; swipe-ul merge si fara capture */
          }
        }

        // Rezistenta la tras: arata ca reactioneaza, fara sa scoata imaginea din cadru.
        this.image.style.transform = 'translateX(' + dx * 0.25 + 'px)';
      });

      const reset = () => {
        down = false;
        dragging = false;
        frame.classList.remove('is-dragging');
        this.image.style.transform = '';
      };

      frame.addEventListener('pointerup', (e) => {
        if (!down) return;
        const wasDragging = dragging;
        const dx = e.clientX - startX;
        reset();
        if (wasDragging && Math.abs(dx) > THRESHOLD) this.show(this.index + (dx < 0 ? 1 : -1));
      });

      frame.addEventListener('pointercancel', reset);
      frame.addEventListener('pointerleave', reset);
    }

    open(index) {
      this.show(index);
      if (!this.dialog.open) this.dialog.showModal();
    }

    show(index) {
      const total = this.openers.length;
      const idx = Math.max(0, Math.min(index, total - 1));
      const opener = this.openers[idx];
      if (!opener) return;

      this.index = idx;

      // Cardurile cu clip afiseaza <video>, cele cu poza <img>. Comutarea e pe
      // acelasi cadru, deci layoutul si butoanele nu se schimba.
      const videoSources = opener.dataset.lbVideoSources;
      const isVideo = !!(this.video && videoSources);
      if (this.video) {
        this.video.hidden = !isVideo;
        this.video.pause();
      }
      if (this.image) this.image.hidden = isVideo;

      if (isVideo) {
        let sources = [];
        try {
          sources = JSON.parse(videoSources);
        } catch (e) {
          sources = [];
        }
        this.video.innerHTML = '';
        sources.forEach((s) => {
          const el = document.createElement('source');
          el.src = s.url;
          if (s.mime_type) el.type = s.mime_type;
          this.video.appendChild(el);
        });
        if (opener.dataset.lbPoster) this.video.poster = opener.dataset.lbPoster;
        if (opener.dataset.lbW && opener.dataset.lbH) {
          this.video.width = opener.dataset.lbW;
          this.video.height = opener.dataset.lbH;
        }
        if (this.frame) {
          this.frame.style.width = this.renderedWidth(opener) + 'px';
          this.frame.style.backgroundImage = 'none';
        }
        this.video.load();
        this.video.play().catch(() => {});
      }

      if (this.image && !isVideo) {
        const img = this.image;

        // Miniatura din card e deja in cache, deci se vede ceva instant.
        if (this.frame) {
          const cardImg = opener.querySelector('img');
          const cached = cardImg ? cardImg.currentSrc || cardImg.src : '';
          this.frame.style.backgroundImage = cached ? 'url("' + cached + '")' : 'none';
        }

        img.classList.remove('is-loaded');
        if (opener.dataset.lbW && opener.dataset.lbH) {
          img.width = opener.dataset.lbW;
          img.height = opener.dataset.lbH;
        }
        const shownWidth = this.renderedWidth(opener);
        if (this.frame) this.frame.style.width = shownWidth + 'px';
        img.sizes = shownWidth + 'px';
        img.srcset = opener.dataset.lbSrcset || '';
        img.src = opener.dataset.lbSrc || '';
        img.alt = opener.dataset.lbAlt || '';
        opener.dataset.lbPrefetched = '1';

        const reveal = () => img.classList.add('is-loaded');
        if (img.complete && img.naturalWidth > 0) reveal();
        else img.addEventListener('load', reveal, { once: true });
      }

      // Vecinii se descarca in fundal, ca sagetile sa nu mai astepte reteaua.
      this.prefetch(idx - 1);
      this.prefetch(idx + 1);

      if (this.titleEl) this.titleEl.textContent = opener.dataset.lbTitle || '';
      if (this.subtitleEl) this.subtitleEl.textContent = opener.dataset.lbSubtitle || '';
      if (this.countEl) this.countEl.textContent = opener.dataset.lbCount || '';

      if (this.linkEl) {
        const href = opener.dataset.lbLink || '';
        this.linkEl.hidden = !href;
        if (href) this.linkEl.href = href;
      }

      if (this.prevBtn) this.prevBtn.disabled = idx === 0;
      if (this.nextBtn) this.nextBtn.disabled = idx === total - 1;

      this.dispatchEvent(
        new CustomEvent('projects-lightbox:change', { bubbles: true, detail: { index: idx } })
      );
    }
  }

  if (!customElements.get('projects-lightbox')) {
    customElements.define('projects-lightbox', ProjectsLightbox);
  }
})();
